import type { EventRule, EventRulesDto, EventStatus, SponsorTier, TeamScoring } from '@rw/shared';
import { formatKm, formatNumber, formatPace } from '../../lib/format';
import { locale, t } from '../../i18n';

export const RULE_LABEL: Record<EventRule, string> = {
  TERRITORY: 'Territory held',
  DISTANCE: 'Distance',
  XP: 'XP',
  CAPTURES: 'Sectors stolen',
};

export const RULE_EXPLAIN: Record<EventRule, string> = {
  TERRITORY: 'Most sectors held on the event map when it ends.',
  DISTANCE: 'Most kilometres of runs that meet the event rules.',
  XP: 'Most XP earned from runs that meet the event rules.',
  CAPTURES: 'Most sectors taken from other participants on the event map.',
};

export const SCORING_LABEL: Record<TeamScoring, string> = {
  TOTAL: 'Team total',
  AVERAGE: 'Team average per member',
};

export function formatEventValue(rule: EventRule, v: number): string {
  if (rule === 'DISTANCE') return `${formatKm(v, 1)} km`;
  if (rule === 'XP') return `${formatNumber(Math.round(v))} XP`;
  return `${Number.isInteger(v) ? formatNumber(v) : v.toFixed(1)} ⬢`;
}

/** "3d 4h left", "starts in 5h", "ended 2 Oct". */
export function eventTiming(status: EventStatus, startsAt: string, endsAt: string, now = Date.now()): string {
  const span = (ms: number) => {
    const m = Math.max(0, Math.round(ms / 60_000));
    const d = Math.floor(m / 1440);
    const h = Math.floor((m % 1440) / 60);
    return d ? `${d}${t('d')} ${h}h` : h ? `${h}h ${m % 60}m` : `${m}m`;
  };
  if (status === 'UPCOMING') return t('Starts in {time}', { time: span(Date.parse(startsAt) - now) });
  if (status === 'ACTIVE') return t('{time} left', { time: span(Date.parse(endsAt) - now) });
  if (status === 'CANCELLED') return t('Cancelled');
  return t('Ended {date}', { date: new Date(endsAt).toLocaleDateString(locale(), { day: 'numeric', month: 'short', year: 'numeric' }) });
}

/** Human list of the organiser's rules, e.g. ["Each run ≥ 2 km", "Pace 4:00–15:00/km"]. */
export function describeRules(r: EventRulesDto): { runs: string[]; finish: string[] } {
  const runs: string[] = [];
  const finish: string[] = [];
  if (r.minRunDistanceM && r.maxRunDistanceM) runs.push(t('Each run {a}–{b} km', { a: formatKm(r.minRunDistanceM, 1), b: formatKm(r.maxRunDistanceM, 1) }));
  else if (r.minRunDistanceM) runs.push(t('Each run at least {a} km', { a: formatKm(r.minRunDistanceM, 1) }));
  else if (r.maxRunDistanceM) runs.push(t('Each run at most {a} km', { a: formatKm(r.maxRunDistanceM, 1) }));
  if (r.minPaceSecPerKm && r.maxPaceSecPerKm) runs.push(t('Pace between {a} and {b} /km', { a: formatPace(r.minPaceSecPerKm), b: formatPace(r.maxPaceSecPerKm) }));
  else if (r.minPaceSecPerKm) runs.push(t('Pace no faster than {a} /km', { a: formatPace(r.minPaceSecPerKm) }));
  else if (r.maxPaceSecPerKm) runs.push(t('Pace no slower than {a} /km', { a: formatPace(r.maxPaceSecPerKm) }));
  if (r.minTotalDistanceM) finish.push(t('{a} km in total', { a: formatKm(r.minTotalDistanceM, 1) }));
  if (r.minRuns) finish.push(t('{n} counted runs', { n: r.minRuns }));
  if (r.minSectors) finish.push(t('{n} different sectors', { n: r.minSectors }));
  return { runs, finish };
}

/** What a runner still lacks to qualify, e.g. "1/2 counted runs" (empty once qualified). */
export function qualificationGaps(r: EventRulesDto, s: { runs: number; distanceM: number; sectors: number }): string[] {
  const gaps: string[] = [];
  if (r.minRuns && s.runs < r.minRuns) gaps.push(t('{have}/{need} counted runs', { have: s.runs, need: r.minRuns }));
  if (r.minTotalDistanceM && s.distanceM < r.minTotalDistanceM)
    gaps.push(t('{have}/{need} km in total', { have: formatKm(s.distanceM, 1), need: formatKm(r.minTotalDistanceM, 1) }));
  if (r.minSectors && s.sectors < r.minSectors) gaps.push(t('{have}/{need} different sectors', { have: s.sectors, need: r.minSectors }));
  return gaps;
}

export const MEDAL = ['🥇', '🥈', '🥉'];

export const toLocalInput = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);

export const inviteLink = (code: string) => `${window.location.origin}/e/${code}`;

/** Sponsor tiers, highest first. */
export const TIER_LABEL: Record<SponsorTier, string> = { DIAMOND: 'Diamond sponsor', GOLD: 'Gold sponsor', SILVER: 'Silver sponsor', PARTNER: 'Partner' };
